import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { Link } from "react-router-dom";
import {
  Heart, Phone, Mail, ArrowUpRight, Sparkles, Users, Star,
  Music, BookOpen, HandHeart, Quote, CalendarDays, Cake, Sun,
} from "lucide-react";
import { MembershipApplicationForm } from "@/pages/MembershipApply";
import { FormDownload } from "@/components/FormDownload";

// Real TIOB photos used throughout
const IMG = {
  collage:    "/wp-content/uploads/2025/08/collage8.4-791x1024.jpeg",
  welcome:    "/wp-content/uploads/2023/09/WEL-2-scaled.jpg",
  torah:      "/wp-content/uploads/2025/12/Rabbi-Holding-Torah.jpg",
  member1:    "/wp-content/uploads/2023/09/botom-pic2.jpg",
  member2:    "/wp-content/uploads/2023/09/botom-pic3.jpg",
  bbq1:       "/wp-content/uploads/2025/05/BBQ-15.jpg",
  bbq2:       "/wp-content/uploads/2025/05/BBQ-11.jpg",
  bbq3:       "/wp-content/uploads/2025/05/BBQ-6.jpg",
  bbq4:       "/wp-content/uploads/2025/05/BBQ-1-scaled.jpg",
  bbqFlyer:   "/wp-content/uploads/2026/05/ENDOFYEAROPENHOUSEANDBARBQ.png",
  stars1:     "/wp-content/uploads/2025/02/stars5-225x300.jpeg",
  stars2:     "/wp-content/uploads/2025/02/stars2-225x300.jpeg",
  stars3:     "/wp-content/uploads/2025/02/karen1-300x225.jpg",
  sukkot1:    "/wp-content/uploads/2025/10/sukkot2-scaled.png",
  sukkot2:    "/wp-content/uploads/2025/10/sukkot1-scaled.png",
  simchat1:   "/wp-content/uploads/2025/10/stor1.jpg",
  simchat2:   "/wp-content/uploads/2025/10/stor2.jpg",
  simchat3:   "/wp-content/uploads/2025/10/stor7.jpg",
  tubishvat:  "/wp-content/uploads/2025/02/IMG_20250219_122346-768x1024.jpg",
  classroom:  "/wp-content/uploads/2025/05/classroom1.jpg",
  lilrev:     "/wp-content/uploads/2025/01/lilrev4.jpg",
};

export default function Membership() {
  return (
    <>
      <SEOHead
        title="Membership at the Reform Synagogue in Brevard County"
        description="Become a member of Temple Israel of Brevard - the all-inclusive, LGBTQ+ welcoming Reform synagogue serving Melbourne, FL and Brevard County. Flexible dues, hardship policy, snowbird pricing."
        path="/membership"
      />

      {/* HERO - emotional opening */}
      <PageHero
        eyebrow="Membership · Shalom"
        title="Belong here."
        subtitle="From your first Friday night to your hundredth, Temple Israel of Brevard becomes the place where the year makes sense, where the people know your name, and where your kids - and you - grow up Jewish, together."
      />

      {/* SECTION 1 - Opening narrative with big inviting photo */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-4">★ A Letter From the Community</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              Dear friend who hasn't joined us yet,
            </h2>
            <div className="mt-7 space-y-5 text-[var(--ink-soft)] text-lg leading-relaxed">
              <p>
                Maybe you grew up Jewish and drifted away. Maybe you're raising a
                family and want your kids to know what a sukkah feels like.
                Maybe you've moved to Brevard from somewhere with more
                synagogues and you're looking for the one that feels like home.
                Maybe you're in love with someone Jewish, or you're falling in
                love with Judaism yourself.
              </p>
              <p>
                Whichever door you came in through - <strong className="text-[var(--blue-ink)]">it's the right
                door</strong>. Walk in any Friday night. Take a seat anywhere.
                Sing if you know the tune, hum if you don't. After the service,
                stay for cookies and coffee, and we'll introduce ourselves.
              </p>
              <p>
                Then come back. And come back again. That's how it starts.
              </p>
              <p className="font-display text-2xl text-[var(--blue-ink)] mt-6">
                Your seat is waiting.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#apply" className="btn-primary" data-testid="membership-apply-cta">
                Apply for Membership <ArrowUpRight size={18} />
              </a>
              <Link to="/worship/shabbat" className="btn-ghost" data-testid="membership-visit-cta">
                Plan your first visit
              </Link>
              <a href="tel:+15852819222" className="btn-ghost">
                <Phone size={16} /> Questions? Call Barb
              </a>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 aspect-square">
              <div className="col-span-4 row-span-4 relative">
                <div aria-hidden="true" className="absolute -inset-2 bg-[var(--blue)]/10 rounded-[2rem] rotate-[-3deg]" />
                <img src={IMG.collage} alt="Snapshots of TIOB life" className="relative rounded-[2rem] shadow-xl w-full h-full object-cover" />
              </div>
              <img src={IMG.welcome} alt="A warm welcome" className="col-span-2 row-span-3 rounded-[1.5rem] shadow-lg w-full h-full object-cover" />
              <img src={IMG.bbq2} alt="Bounce house joy at TIOB BBQ" className="col-span-2 row-span-3 rounded-[1.5rem] shadow-lg w-full h-full object-cover" />
              <img src={IMG.simchat1} alt="Dancing with the Torah on Simchat Torah" className="col-span-4 row-span-2 rounded-[1.5rem] shadow-lg w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* APPLY - inline application form */}
      <section id="apply" className="bg-[#EAF3FB] py-16 lg:py-24 border-t border-[var(--line-warm)]" data-testid="membership-apply-section">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <div className="text-center mb-10">
            <p className="eyebrow mb-4">★ Become a Member</p>
            <h2 className="font-display text-3xl md:text-4xl text-[var(--blue-ink)] leading-tight">
              Ready to join? Start here.
            </h2>
            <p className="mt-4 text-[var(--ink-soft)] text-lg">
              Fill out the application below - Terry and Barb will follow up personally
              within 2-3 business days.
            </p>
          </div>
          <MembershipApplicationForm />
        </div>
      </section>

      {/* SECTION 2 - The Promise / Feel cards */}
      <section className="bg-[var(--cream)] py-20 lg:py-28 border-y border-[var(--line-warm)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">★ What Membership Feels Like</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              Six things you'll feel by month three.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeelCard icon={Heart} title="Known" body="At your second visit, someone will remember your name. At your fifth, they'll save you a seat." />
            <FeelCard icon={Music}  title="Lifted"  body="There's something about singing a melody together that you didn't know you needed - until you do." />
            <FeelCard icon={Users}  title="Held"    body="Bad week? Loss in the family? Big simcha? The TIOB casserole-and-card brigade is real, and it's coming for you." />
            <FeelCard icon={Cake}   title="Fed"     body="Oneg cookies. Passover seders. End-of-year BBQ with bounce houses. Shared meals after Torah Study. We eat well together." />
            <FeelCard icon={BookOpen} title="Curious" body="Adult Hebrew. Weekly Torah Study. A Journey of Discovery. The learning never stops - and it's actually fun." />
            <FeelCard icon={HandHeart} title="Useful" body="Volunteer for the food pantry, sing in the choir, sit on the board, bake a kugel for shiva. Show up, change a corner of the world." />
          </div>
        </div>
      </section>

      {/* SECTION 3 - Year in the Life mosaic */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-3xl mb-12 mx-auto text-center">
            <p className="eyebrow mb-4">★ A Year At TIOB</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              The Jewish calendar, lived in full color.
            </h2>
            <p className="mt-5 text-[var(--ink-soft)] text-lg">
              Membership unlocks every one of these moments - plus the friendships
              that hold them together.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <YearItem img={IMG.stars1} season="Winter" label="Shabbat Under the Stars" />
            <YearItem img={IMG.tubishvat} season="Spring" label="Tu B'Shevat Seder" />
            <YearItem img={IMG.bbq3} season="Late Spring" label="Religious School Picnic" />
            <YearItem img={IMG.bbqFlyer} season="Summer" label="End-of-Year Open House & BBQ" objectFit="cover" />
            <YearItem img={IMG.sukkot1} season="Fall" label="Sukkot - Shake, Shake, Shake" />
            <YearItem img={IMG.simchat2} season="Fall" label="Simchat Torah" />
            <YearItem img={IMG.lilrev} season="Anytime" label="Guest Concerts" />
            <YearItem img={IMG.torah} season="Every Week" label="Friday Night Shabbat" />
          </div>
        </div>
      </section>

      {/* SECTION 4 - Anchor Quote */}
      <section className="bg-[var(--blue-ink)] py-20 lg:py-24 text-white relative overflow-hidden">
        <div aria-hidden="true" className="absolute -top-24 -left-24 w-96 h-96 bg-[var(--blue-bright)]/20 blob" />
        <div aria-hidden="true" className="absolute -bottom-24 -right-24 w-80 h-80 bg-[var(--honey)]/25 blob" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <Quote size={42} className="mx-auto text-[var(--honey)] mb-6" />
          <blockquote className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight text-white">
            "Find yourself a rabbi, acquire for yourself a friend, and judge every
            person on the positive side."
          </blockquote>
          <p className="mt-6 font-mono-accent text-xs tracking-[0.3em] uppercase text-[var(--blue-bright)]">
            ★ Pirke Avot 1:6 - The instruction manual for TIOB membership
          </p>
        </div>
      </section>

      {/* SECTION 5 - Three small stories */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">★ Why People Join</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              No two paths to TIOB look the same.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <StoryCard
              img={IMG.member1}
              chip="The Snowbird"
              title="Half the year in Brevard - all year a member."
              body="Snowbirds make TIOB their winter home with pro-rated dues. Sit in your usual seat from December through April, take Shabbat services on Zoom from up north May through November, and feel rooted year-round."
            />
            <StoryCard
              img={IMG.classroom}
              chip="The Family"
              title="Religious school, but make it joyful."
              body="Sunday mornings, kids learn Hebrew, holidays, and ethics in small classes where every child is seen. Parents drop coffee and stay for adult learning, or trade carpool duties with friends they'll know for life."
            />
            <StoryCard
              img={IMG.welcome}
              chip="The Seeker"
              title="A Journey of Discovery - at your pace."
              body="Whether you're converting, returning, or just curious, Rabbi Fanwick walks alongside you. Many of our most active members started by quietly slipping into a back row pew. There's no test. There's only welcome."
            />
          </div>
        </div>
      </section>

      {/* SECTION 6 - What you get */}
      <section className="bg-[var(--cream)] py-20 lg:py-28 border-y border-[var(--line-warm)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div aria-hidden="true" className="absolute -inset-3 bg-[var(--honey)]/25 rounded-[2rem] rotate-2" />
              <img src={IMG.bbq1} alt="The TIOB chefs at the end-of-year BBQ" className="relative rounded-[2rem] shadow-xl w-full" loading="lazy" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow mb-4">★ Member Benefits</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              What's included in your membership.
            </h2>
            <ul className="mt-7 grid sm:grid-cols-2 gap-4">
              {[
                ["Reserved High Holy Days seats", "No separate ticket purchase needed."],
                ["B'nei mitzvah preparation", "Rabbi works one-on-one with your child."],
                ["Lifecycle ceremonies", "Weddings, baby namings, funerals, shiva."],
                ["Religious school", "K - 7 included FREE with your family membership."],
                ["Adult education", "Hebrew, weekly Torah Study, all included."],
                ["Member events", "Seders, BBQs, Sisterhood, Men's Club."],
                ["Pastoral care", "Anytime you need to talk - the rabbi is there."],
                ["The bulletin", "Weekly news, yahrzeits, and announcements."],
              ].map(([t, d]) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1 shrink-0 w-7 h-7 rounded-full bg-[var(--blue)]/10 text-[var(--blue-deep)] flex items-center justify-center">
                    <Star size={14} className="fill-[var(--honey)] text-[var(--honey)]" />
                  </span>
                  <div>
                    <p className="font-semibold text-[var(--blue-ink)]">{t}</p>
                    <p className="text-sm text-[var(--ink-soft)]">{d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 7 - Dues with empathy */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="eyebrow mb-4">★ Annual Dues</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              Built around real life.
            </h2>
            <p className="mt-5 text-[var(--ink-soft)] text-lg">
              Pay monthly or annually. Ask about Snowbird pro-rating. And if money
              is the only thing standing between your family and TIOB -{" "}
              <strong className="text-[var(--blue-ink)]">it isn't</strong>. Our
              hardship policy is real and quietly handled with dignity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            <DuesCard label="Family - Two-Adult Household" amount="$2,200" />
            <DuesCard label="Family - Single Parent" amount="$1,100" />
            <DuesCard label="Adult Couple (no children)" amount="$2,200" />
            <DuesCard label="Adult Single" amount="$1,400" />
            <DuesCard label="Young Adult Couple/Family (under 36)" amount="$1,400" />
            <DuesCard label="Young Adult Single (under 36)" amount="$1,100" />
            <DuesCard label="Senior Couple (65+)" amount="$1,870" />
            <DuesCard label="Senior Single (65+)" amount="$1,210" />
            <DuesCard label="Snowbird" amount="Pro-rated" hint="Full year dues × months here ÷ 12" />
          </div>

          <p className="text-center mt-8 text-sm text-[var(--muted)]">
            Hardship reductions are available with documentation - please reach
            out, no questions asked.
          </p>
        </div>
      </section>

      {/* SECTION 8 - All means all */}
      <section className="bg-[var(--cream)] py-20 lg:py-28 border-y border-[var(--line-warm)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img src={IMG.bbq4} alt="Snow cones at the BBQ" className="rounded-2xl shadow-md w-full h-48 md:h-56 object-cover" loading="lazy" />
            <img src={IMG.stars2} alt="Shabbat under the stars" className="rounded-2xl shadow-md w-full h-48 md:h-56 object-cover" loading="lazy" />
            <img src={IMG.simchat3} alt="Joyful Simchat Torah" className="rounded-2xl shadow-md w-full h-48 md:h-56 object-cover" loading="lazy" />
            <img src={IMG.sukkot2} alt="Sukkot at TIOB" className="rounded-2xl shadow-md w-full h-48 md:h-56 object-cover" loading="lazy" />
          </div>
          <div>
            <p className="eyebrow mb-4">★ All Means All</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              You are welcome here. Yes, you.
            </h2>
            <p className="mt-6 text-[var(--ink-soft)] text-lg leading-relaxed">
              TIOB is a home for interfaith families, LGBTQ+ individuals, Jews
              of color, singles, couples, seniors, snowbirds, converts, the
              Jew-curious, the long-lapsed, the still-figuring-it-out. We have
              made the conscious, joyful choice to be a place where every kind
              of seeker belongs.
            </p>
            <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">
              You don't need to read Hebrew. You don't need to have grown up
              going to temple. You don't need to know what to wear. Just come
              once. We'll handle the rest.
            </p>
            <Link to="/contact" className="btn-warm mt-8" data-testid="membership-all-cta">
              <Heart size={16} /> Send us a note
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 9 - Talk to Barb (BIG CTA) */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <div className="relative rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-[var(--blue-ink)] to-[var(--blue-deep)] text-white p-10 md:p-16 shadow-2xl">
            <div aria-hidden="true" className="absolute -top-20 -right-20 w-80 h-80 bg-[var(--honey)]/20 blob" />
            <div aria-hidden="true" className="absolute -bottom-24 -left-24 w-96 h-96 bg-[var(--blue-bright)]/20 blob" />
            <div className="relative grid md:grid-cols-5 gap-10 items-center">
              <div className="md:col-span-3">
                <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[var(--blue-bright)] mb-4">
                  ★ Take The First Step
                </p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
                  Have a coffee with Barb.
                </h2>
                <p className="mt-5 text-white/80 text-lg leading-relaxed">
                  Our Membership Coordinator, Barb Cannon, would love to answer
                  your questions, hear your story, and figure out together
                  whether TIOB is the right home for you. No pressure, no
                  pitch - just a friendly conversation.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <a href="mailto:BLCannon527@gmail.com" className="btn-warm" data-testid="membership-email">
                    <Mail size={16} /> Email Barb
                  </a>
                  <a href="tel:+15852819222" className="btn-ghost !bg-transparent !text-white !border-white hover:!bg-white hover:!text-[var(--blue-ink)]" data-testid="membership-phone">
                    <Phone size={16} /> (585) 281-9222
                  </a>
                </div>
                <p className="mt-6 text-sm text-white/60">
                  Prefer the office?{" "}
                  <a href="mailto:tioffice@tiofbrevard.com" className="underline hover:text-white">
                    tioffice@tiofbrevard.com
                  </a>{" "}
                  · (321) 631-9494
                </p>
              </div>
              <div className="md:col-span-2">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6">
                  <p className="font-mono-accent text-xs uppercase tracking-widest text-[var(--blue-bright)] mb-3">★ Quick Facts</p>
                  <ul className="space-y-3 text-sm">
                    <Fact icon={CalendarDays} text="Visit any Friday at 7 PM" />
                    <Fact icon={Sun}          text="Snowbirds welcomed with pro-rated dues" />
                    <Fact icon={Sparkles}     text="Hardship policy - real, quiet, no shame" />
                    <Fact icon={Heart}        text="Member of the Union for Reform Judaism" />
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 10 - Printable form */}
      <section className="bg-white py-16 border-t border-[var(--line-warm)]" data-testid="membership-form-download-section">
        <div className="max-w-2xl mx-auto px-5 lg:px-10">
          <div className="text-center mb-6">
            <p className="eyebrow mb-3">★ Prefer Paper?</p>
            <h2 className="font-display text-2xl md:text-3xl text-[var(--blue-ink)]">
              Download the printable membership application.
            </h2>
          </div>
          <FormDownload
            label="2026 Membership Application"
            file="/forms/membership-application.pdf"
            description="Fillable PDF - download, complete offline, and email to tioffice@tiofbrevard.com or drop off at the temple office."
          />
        </div>
      </section>
    </>
  );
}

function FeelCard({ icon: Icon, title, body }) {
  return (
    <div className="card-soft p-7 bg-white">
      <div className="w-14 h-14 rounded-2xl bg-[var(--honey)]/25 text-[var(--blue-ink)] flex items-center justify-center mb-5">
        <Icon size={22} />
      </div>
      <h3 className="font-display text-2xl text-[var(--blue-ink)]">{title}</h3>
      <p className="mt-2 text-[var(--ink-soft)] leading-relaxed">{body}</p>
    </div>
  );
}

function YearItem({ img, season, label, objectFit = "cover" }) {
  return (
    <figure className="relative rounded-2xl overflow-hidden aspect-[4/5] group">
      <img src={img} alt={label} className={`w-full h-full object-${objectFit} transition-transform duration-500 group-hover:scale-105`} loading="lazy" />
      <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-[var(--blue-ink)]/95 via-[var(--blue-ink)]/55 to-transparent text-white">
        <p className="font-mono-accent text-xs uppercase tracking-widest text-[var(--blue-bright)]">{season}</p>
        <p className="font-display text-lg leading-tight mt-0.5">{label}</p>
      </figcaption>
    </figure>
  );
}

function StoryCard({ img, chip, title, body }) {
  return (
    <article className="card-soft overflow-hidden bg-white flex flex-col">
      <img src={img} alt={chip} className="w-full h-56 object-cover" loading="lazy" />
      <div className="p-6 flex-1 flex flex-col">
        <span className="self-start font-mono-accent text-xs uppercase tracking-widest bg-[var(--blue)]/10 text-[var(--blue-deep)] px-2.5 py-1 rounded-full">{chip}</span>
        <h3 className="font-display text-2xl text-[var(--blue-ink)] mt-3 leading-tight">{title}</h3>
        <p className="mt-3 text-[var(--ink-soft)] text-sm leading-relaxed flex-1">{body}</p>
      </div>
    </article>
  );
}

function DuesCard({ label, amount, hint, highlight }) {
  return (
    <div className={`card-soft p-6 bg-white relative ${highlight ? "ring-2 ring-[var(--honey)]" : ""}`}>
      {highlight && (
        <span className="absolute -top-3 left-6 bg-[var(--honey)] text-[var(--blue-ink)] text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
          Most flexible
        </span>
      )}
      <p className="text-sm text-[var(--muted)] font-medium">{label}</p>
      <p className="font-display text-3xl text-[var(--blue-ink)] mt-2">
        {amount}
        {amount.startsWith("$") && <span className="text-base text-[var(--ink-soft)]"> / yr</span>}
      </p>
      {hint && <p className="text-xs text-[var(--muted)] mt-2">{hint}</p>}
    </div>
  );
}

function Fact({ icon: Icon, text }) {
  return (
    <li className="flex items-start gap-3 text-white/90">
      <Icon size={16} className="mt-0.5 shrink-0 text-[var(--blue-bright)]" />
      <span>{text}</span>
    </li>
  );
}
